import type { ApiResponse, WorkerRole, WorkerTier } from '../../core/index.js'
import type { BehaviorEvent, EffectiveBehavior } from '../../behaviors/index.js'
import type { GenerateSummaryInput, PerformanceSummary, PerformanceSummaryListItem } from '../../../features/performance_reviews/types.js'
import type { RankingDecision, ShiftOutcome } from '../index.js'
import type { BridgeCallout, CensusEntryInput, CensusRequirementsView, CensusView, ClinicalBridgeStatus, FacilityDrafts, FacilityShiftPreferences, RecordCensusResult, ScheduleRun, SchedulePeriodView, ScheduleBoardView, ScheduleCoverageView } from '../../schedule/index.js'
import type { WorkerProfile } from '../../roster/index.js'
import type { WorkerScore, WorkerStanding } from '../../scoring/index.js'
import { ApiError } from '../../core/index.js'
import { signEs256Jwt } from '../index.js'
import { inspectedFetch } from '../../core/index.js'
import type { LoadedPartnerKey } from './partnerKeyFile.js'

const CLIENT_ASSERTION_TYPE = 'urn:ietf:params:oauth:client-assertion-type:jwt-bearer'
// Well inside the API's five-minute maximum - the assertion is used once, immediately.
const ASSERTION_LIFETIME_SECONDS = 60

/** What the partner simulator holds after `POST /oauth/token` - a machine token acting as one client. */
export interface PartnerSession {
  clientId: string
  accessToken: string
  scopes: string[]
  expiresAt: string
  /** The token's payload, decoded for display. Not verified here - the API verifies it on every call. */
  claims: Record<string, unknown>
}

/** Wire format of `POST /events/shift-complete` (snake_case, the partner's convention). */
export interface ShiftCompleteEvent {
  facility_id: string
  worker_id: string
  shift_date: string
  scheduled_start: string
  actual_start: string
  scheduled_end: string
  actual_end: string
  unit_id: string
  /** True when the shift was Clinical Bridge (on-call) coverage. Defaults to false. */
  clinical_bridge?: boolean
  /** Whether the shift's chart was completed: one shift, one chart. Left out when not known, which is not a missed chart. */
  chart_completed?: boolean
}

/** Wire format of `POST /events/no-show`. */
export type NoShowEvent = Omit<ShiftCompleteEvent, 'actual_start' | 'actual_end'>

/** Wire format of `POST /events/callout`. `reported_at` is when the worker gave notice. */
export type CalloutEvent = NoShowEvent & { reported_at: string }

/** Wire format of `POST /events/worker-onboard`. */
export interface WorkerOnboardEvent {
  facility_id: string
  worker_id: string
  name: string
  role: WorkerRole
  hire_date: string
  initial_tier?: WorkerTier
}

function decodeClaims(jwt: string): Record<string, unknown> {
  try {
    const payload = jwt.split('.')[1] ?? ''
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const claims: unknown = JSON.parse(json)
    return claims !== null && typeof claims === 'object' ? (claims as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

/**
 * The partner's side of the `client_credentials` grant: signs a
 * `private_key_jwt` client assertion with the loaded key and exchanges it for
 * a bearer token. `issuer` is the assertion's audience. Leaving `scope` empty
 * asks for every scope the client was approved for.
 */
export async function requestPartnerToken(
  baseUrl: string,
  issuer: string,
  clientId: string,
  key: LoadedPartnerKey,
  scope: string,
): Promise<PartnerSession> {
  const issuedAt = Math.floor(Date.now() / 1000)
  const assertion = await signEs256Jwt(
    key.kid,
    { iss: clientId, sub: clientId, aud: issuer, iat: issuedAt, exp: issuedAt + ASSERTION_LIFETIME_SECONDS, jti: crypto.randomUUID() },
    key.signingKey,
  )

  const form = new URLSearchParams({ grant_type: 'client_credentials', client_assertion_type: CLIENT_ASSERTION_TYPE, client_assertion: assertion })
  if (scope !== '') {
    form.set('scope', scope)
  }

  const response = await inspectedFetch(`${baseUrl}/oauth/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: form.toString(),
  })

  // This endpoint speaks standard OAuth shapes, not the API's usual envelope.
  let body: { access_token?: string; expires_in?: number; scope?: string; error?: string; error_description?: string } | null = null
  try {
    body = await response.json()
  } catch {
    // Non-JSON body - falls through to the generic failure below.
  }

  if (!response.ok || !body?.access_token || typeof body.expires_in !== 'number') {
    throw new ApiError(body?.error_description ?? body?.error ?? `Token request failed (${response.status})`, response.status)
  }

  return {
    clientId,
    accessToken: body.access_token,
    scopes: (body.scope ?? '').split(' ').filter(Boolean),
    expiresAt: new Date(Date.now() + body.expires_in * 1000).toISOString(),
    claims: decodeClaims(body.access_token),
  }
}

async function callAsPartner<T>(
  baseUrl: string,
  session: PartnerSession,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  payload?: unknown,
  extraHeaders: Record<string, string> = {},
): Promise<T> {
  const response = await inspectedFetch(`${baseUrl}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${session.accessToken}`,
      ...(payload === undefined ? {} : { 'content-type': 'application/json' }),
      ...extraHeaders,
    },
    body: payload === undefined ? undefined : JSON.stringify(payload),
  })

  let body: ApiResponse<T> | null = null
  try {
    body = (await response.json()) as ApiResponse<T>
  } catch {
    // Non-JSON body - falls through to the generic failure below.
  }

  if (!response.ok || !body?.success || body.data === undefined) {
    throw new ApiError(body?.error ?? `Request to ${path} failed (${response.status})`, response.status)
  }
  return body.data
}

/** What the API answers to a shift-outcome event. `duplicate` is true for a retry it had already stored. */
export interface EventAccepted {
  eventId: string
  received: true
  duplicate: boolean
}

/** An empty key sends no `Idempotency-Key` header, so every send is stored as a new outcome. */
function idempotencyHeaders(idempotencyKey: string): Record<string, string> {
  return idempotencyKey === '' ? {} : { 'Idempotency-Key': idempotencyKey }
}

export function sendShiftComplete(baseUrl: string, session: PartnerSession, event: ShiftCompleteEvent, idempotencyKey = ''): Promise<EventAccepted> {
  return callAsPartner(baseUrl, session, 'POST', '/events/shift-complete', event, idempotencyHeaders(idempotencyKey))
}

export function sendCallout(baseUrl: string, session: PartnerSession, event: CalloutEvent, idempotencyKey = ''): Promise<EventAccepted> {
  return callAsPartner(baseUrl, session, 'POST', '/events/callout', event, idempotencyHeaders(idempotencyKey))
}

export function sendNoShow(baseUrl: string, session: PartnerSession, event: NoShowEvent, idempotencyKey = ''): Promise<EventAccepted> {
  return callAsPartner(baseUrl, session, 'POST', '/events/no-show', event, idempotencyHeaders(idempotencyKey))
}

export function sendWorkerOnboard(baseUrl: string, session: PartnerSession, event: WorkerOnboardEvent): Promise<WorkerProfile> {
  return callAsPartner(baseUrl, session, 'POST', '/events/worker-onboard', event)
}

/** Reads back what the API has stored for a worker - any token for the client may, so the partner's own does. */
export function listWorkerOutcomes(baseUrl: string, session: PartnerSession, workerId: string): Promise<ShiftOutcome[]> {
  return callAsPartner(baseUrl, session, 'GET', `/workers/${encodeURIComponent(workerId)}/outcomes`)
}

/** What this facility measures and how much each behavior counts - how a partner learns which events it is using. */
export function getFacilityBehaviors(baseUrl: string, session: PartnerSession, facilityId: string): Promise<EffectiveBehavior[]> {
  return callAsPartner(baseUrl, session, 'GET', `/behaviors/facilities/${encodeURIComponent(facilityId)}`)
}

/** Wire format of `POST /events/behavior`: one occurrence of a behavior the client defined. */
export interface CustomBehaviorEvent {
  facility_id: string
  worker_id: string
  behavior_key: string
  occurred_at: string
  shift_date?: string
  note?: string
}

export function sendBehaviorEvent(baseUrl: string, session: PartnerSession, event: CustomBehaviorEvent, idempotencyKey = ''): Promise<EventAccepted> {
  return callAsPartner(baseUrl, session, 'POST', '/events/behavior', event, idempotencyHeaders(idempotencyKey))
}

export function listWorkerBehaviorEvents(baseUrl: string, session: PartnerSession, workerId: string): Promise<BehaviorEvent[]> {
  return callAsPartner(baseUrl, session, 'GET', `/workers/${encodeURIComponent(workerId)}/behavior-events`)
}

/** A worker's Clinical Bridge record: eligibility, the days they would cover and the facility's rules. Any token for the client may read it. */
export function getWorkerClinicalBridge(baseUrl: string, session: PartnerSession, workerId: string): Promise<ClinicalBridgeStatus> {
  return callAsPartner(baseUrl, session, 'GET', `/workers/${encodeURIComponent(workerId)}/clinical-bridge`)
}

const bridgeDatePath = (workerId: string, date: string): string => `/workers/${encodeURIComponent(workerId)}/clinical-bridge/dates/${encodeURIComponent(date)}`

/** `PUT /workers/{workerId}/clinical-bridge/dates/{date}` as the partner: the scheduler records a day the worker chose in its bid screen (`events:write`). Answers the record. */
export function enrollBridgeDate(baseUrl: string, session: PartnerSession, workerId: string, date: string): Promise<ClinicalBridgeStatus> {
  return callAsPartner(baseUrl, session, 'PUT', bridgeDatePath(workerId, date))
}

/** `DELETE .../clinical-bridge/dates/{date}` as the partner: takes a day back off the worker's list. Answers the record. */
export function withdrawBridgeDate(baseUrl: string, session: PartnerSession, workerId: string, date: string): Promise<ClinicalBridgeStatus> {
  return callAsPartner(baseUrl, session, 'DELETE', bridgeDatePath(workerId, date))
}

/** A facility's Clinical Bridge callouts, newest first: the partner's way to learn who took a shift. Needs `events:write` or `rankings:read`; the first page. */
export async function listBridgeCallouts(baseUrl: string, session: PartnerSession, facilityId: string): Promise<BridgeCallout[]> {
  return callAsPartner(baseUrl, session, 'GET', `/facilities/${encodeURIComponent(facilityId)}/bridge/callouts?limit=50`)
}

/** `GET /facilities/{facilityId}/schedule/drafts` as the partner: every worker's draft for next month, accepted or not. */
export async function listDrafts(baseUrl: string, session: PartnerSession, facilityId: string): Promise<FacilityDrafts> {
  return callAsPartner(baseUrl, session, 'GET', `/facilities/${encodeURIComponent(facilityId)}/schedule/drafts`)
}

/** `GET /facilities/{facilityId}/shift-preferences` as the partner: every worker's preferences for next month, what a scheduler builds from. */
export async function listShiftPreferences(baseUrl: string, session: PartnerSession, facilityId: string): Promise<FacilityShiftPreferences> {
  return callAsPartner(baseUrl, session, 'GET', `/facilities/${encodeURIComponent(facilityId)}/shift-preferences`)
}

/** `GET /facilities/{facilityId}/schedule/period` as the partner: where the month stands and the released run, if any. */
export async function getSchedulePeriod(baseUrl: string, session: PartnerSession, facilityId: string): Promise<SchedulePeriodView> {
  return callAsPartner(baseUrl, session, 'GET', `/facilities/${encodeURIComponent(facilityId)}/schedule/period`)
}

/** `GET /facilities/{facilityId}/schedule/runs/{runId}` as the partner: one run in full - what its scheduler would take as the month. */
export async function getScheduleRun(baseUrl: string, session: PartnerSession, facilityId: string, runId: string): Promise<ScheduleRun> {
  return callAsPartner(baseUrl, session, 'GET', `/facilities/${encodeURIComponent(facilityId)}/schedule/runs/${encodeURIComponent(runId)}`)
}

/** `GET /facilities/{facilityId}/schedule/board` as the partner: the released month's board - what moved after the release. */
export async function getScheduleBoard(baseUrl: string, session: PartnerSession, facilityId: string, period?: string): Promise<ScheduleBoardView> {
  return callAsPartner(baseUrl, session, 'GET', `/facilities/${encodeURIComponent(facilityId)}/schedule/board${period ? `?period=${encodeURIComponent(period)}` : ''}`)
}

/** What a partner would fetch to show a worker their standing inside its own app. Any token for the client may. */
export function getWorkerStanding(baseUrl: string, session: PartnerSession, workerId: string): Promise<WorkerStanding> {
  return callAsPartner(baseUrl, session, 'GET', `/workers/${encodeURIComponent(workerId)}/standing`)
}

/** The worker's current running score, which every stored event moves. Any token for the client may read it. */
export function getWorkerScore(baseUrl: string, session: PartnerSession, workerId: string): Promise<WorkerScore> {
  return callAsPartner(baseUrl, session, 'GET', `/workers/${encodeURIComponent(workerId)}/score`)
}

/** Asks a facility to rank a pool the partner has already qualified. Needs the `rankings:read` scope. */
export function requestRanking(
  baseUrl: string,
  session: PartnerSession,
  facilityId: string,
  request: { shift_ref: string; worker_ids: string[] },
): Promise<RankingDecision> {
  return callAsPartner(baseUrl, session, 'POST', `/facilities/${encodeURIComponent(facilityId)}/rankings`, request)
}

/** Generates a performance summary as the client's HR integration would. Needs the `performance:read` scope and the add-on enabled at the worker's facility. */
export function generatePerformanceSummary(baseUrl: string, session: PartnerSession, workerId: string, input: GenerateSummaryInput): Promise<PerformanceSummary> {
  return callAsPartner(baseUrl, session, 'POST', `/workers/${encodeURIComponent(workerId)}/performance-summaries`, input)
}

/** A worker's summaries, newest first, without their content. Needs `performance:read`. */
export function listPerformanceSummaries(baseUrl: string, session: PartnerSession, workerId: string): Promise<PerformanceSummaryListItem[]> {
  return callAsPartner(baseUrl, session, 'GET', `/workers/${encodeURIComponent(workerId)}/performance-summaries`)
}

/** `GET /facilities/{facilityId}/schedule/coverage` as the partner: the month against the facility's minimum staffing (phase D). */
export async function getScheduleCoverage(baseUrl: string, session: PartnerSession, facilityId: string, period?: string): Promise<ScheduleCoverageView> {
  return callAsPartner(baseUrl, session, 'GET', `/facilities/${encodeURIComponent(facilityId)}/schedule/coverage${period ? `?period=${encodeURIComponent(period)}` : ''}`)
}

/** `GET /facilities/{facilityId}/census?from&to` as the partner: every date of the range per unit, recorded or forecast (census response plan F0). */
export function getCensus(baseUrl: string, session: PartnerSession, facilityId: string, range?: { from: string; to: string }): Promise<CensusView> {
  return callAsPartner(baseUrl, session, 'GET', `/facilities/${encodeURIComponent(facilityId)}/census${range ? `?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}` : ''}`)
}

/** `PUT /facilities/{facilityId}/census` as the partner - its EHR's ADT feed writing the day's counts. Needs `census:write`. */
export function recordCensus(baseUrl: string, session: PartnerSession, facilityId: string, entries: CensusEntryInput[]): Promise<RecordCensusResult> {
  return callAsPartner(baseUrl, session, 'PUT', `/facilities/${encodeURIComponent(facilityId)}/census`, { entries })
}

/** `GET /facilities/{facilityId}/census/requirements?date&unitId&census` as the partner: what a date requires under the facility's staffing plan. */
export function getCensusRequirements(baseUrl: string, session: PartnerSession, facilityId: string, query: { date: string; unitId?: string; census?: number }): Promise<CensusRequirementsView> {
  const search = new URLSearchParams({ date: query.date, ...(query.unitId ? { unitId: query.unitId } : {}), ...(query.census !== undefined ? { census: String(query.census) } : {}) })
  return callAsPartner(baseUrl, session, 'GET', `/facilities/${encodeURIComponent(facilityId)}/census/requirements?${search.toString()}`)
}
